import React, { useEffect, useMemo, useState } from 'react';
import { meApi, taskApi } from '../services/api';
import { ActivityDot, FollowupDot } from '../types';
import { STAGE_LABEL, fmtDayTime, istDay, istToday, label, shiftDay, stageChip } from '../lib';
import { Failed, Loading } from '../components/WorkBits';
import Sheet from '../components/lead/Sheet';
import LeadGlance from '../components/lead/LeadGlance';
import TagExplorer from '../components/TagExplorer';

/** The Nurture app's Dashboard at desktop width: site visits first, then the
 *  follow-ups radar (what's coming, by when) and the activity trail (what you
 *  did), then the tag explorer. The server returns dots over a free range; all
 *  grouping is here, so the range toggles never re-fetch. */

// ── Bucketing (mirrors the app's TimelineBucketing.kt, on IST day keys) ──────

type Bucket<T> = { key: string; label: string; sub?: string; overdue?: boolean; items: T[] };

const dayLabel = (d: string, fmt: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-IN', { timeZone: 'UTC', ...fmt }).format(new Date(`${d}T12:00:00Z`));
const short = (d: string) => dayLabel(d, { day: 'numeric', month: 'short' });
const mondayOf = (d: string) => shiftDay(d, -((new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7));
const monthStart = (d: string, plus: number) => {
  const x = new Date(`${d.slice(0, 7)}-01T12:00:00Z`);
  x.setUTCMonth(x.getUTCMonth() + plus);
  return x.toISOString().slice(0, 10);
};

type FRange = '7d' | '4w' | '3m';
const followupBuckets = (dots: FollowupDot[], range: FRange): Bucket<FollowupDot>[] => {
  const now = Date.now();
  const today = istToday();
  const sorted = [...dots].sort((a, b) => a.due_at.localeCompare(b.due_at));
  const overdue = sorted.filter((d) => new Date(d.due_at).getTime() < now);
  const upcoming = sorted.filter((d) => new Date(d.due_at).getTime() >= now);
  const slice = (from: string, to: string) => upcoming.filter((d) => istDay(d.due_at) >= from && istDay(d.due_at) <= to);
  const out: Bucket<FollowupDot>[] = [{ key: 'od', label: 'Overdue', overdue: true, items: overdue }];
  let lastDay: string;
  if (range === '7d') {
    for (let i = 0; i < 7; i++) {
      const d = shiftDay(today, i);
      out.push({ key: d, label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : dayLabel(d, { weekday: 'short' }), sub: short(d), items: slice(d, d) });
    }
    lastDay = shiftDay(today, 6);
  } else if (range === '4w') {
    const mon = mondayOf(today);
    for (let i = 0; i < 4; i++) {
      const start = shiftDay(mon, 7 * i);
      const end = shiftDay(start, 6);
      const from = i === 0 ? today : start;
      out.push({ key: start, label: i === 0 ? 'This week' : i === 1 ? 'Next week' : `Wk of ${short(start)}`, sub: `${short(from)}–${short(end)}`, items: slice(from, end) });
    }
    lastDay = shiftDay(mon, 27);
  } else {
    for (let i = 0; i < 3; i++) {
      const start = monthStart(today, i);
      const end = shiftDay(monthStart(today, i + 1), -1);
      out.push({ key: start, label: dayLabel(start, { month: 'long' }), items: slice(i === 0 ? today : start, end) });
    }
    lastDay = shiftDay(monthStart(today, 3), -1);
  }
  out.push({ key: 'later', label: 'Later', items: upcoming.filter((d) => istDay(d.due_at) > lastDay) });
  return out;
};

type ARange = '7d' | '4w';
const activityBuckets = (dots: ActivityDot[], range: ARange): Bucket<ActivityDot>[] => {
  const today = istToday();
  const sorted = [...dots].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
  const slice = (from: string, to: string) => sorted.filter((d) => istDay(d.occurred_at) >= from && istDay(d.occurred_at) <= to);
  if (range === '7d') {
    return Array.from({ length: 7 }, (_, i) => {
      const d = shiftDay(today, -i);
      return { key: d, label: i === 0 ? 'Today' : i === 1 ? 'Yesterday' : dayLabel(d, { weekday: 'short' }), sub: short(d), items: slice(d, d) };
    });
  }
  const mon = mondayOf(today);
  return Array.from({ length: 4 }, (_, i) => {
    const start = shiftDay(mon, -7 * i);
    const end = i === 0 ? today : shiftDay(start, 6);
    return { key: start, label: i === 0 ? 'This week' : i === 1 ? 'Last week' : `Wk of ${short(start)}`, sub: `${short(start)}–${short(end)}`, items: slice(start, end) };
  });
};

// ── Display vocabulary — the tag keys the app derives its marks from ─────────

const VISIT_INTENT = ['visit_status:committed', 'visit_status:interested_no_date'];
const HOT = ['timeline:immediate', 'finance:self_funded'];

const FILTERS: [string, string, (d: FollowupDot) => boolean][] = [
  ['all', 'All', () => true],
  ['visit', 'Visit intent', (d) => d.tags.some((t) => VISIT_INTENT.includes(t))],
  ['committed', 'Committed', (d) => d.tags.includes('visit_status:committed')],
  ['hot', 'Hot', (d) => d.tags.some((t) => HOT.includes(t))],
  ['call', 'Calls', (d) => d.type === 'call'],
  ['whatsapp', 'WhatsApp', (d) => d.type === 'whatsapp'],
];

/** A stage's dot colour — the same family as its chip everywhere else. */
const DOT: Record<string, string> = {
  'chip-forest': 'bg-forest', 'chip-gold': 'bg-gold', 'chip-sky': 'bg-deep-sky/70',
  'chip-brick': 'bg-brick', 'chip-mute': 'bg-graphite/40',
};

const Glyph: React.FC<{ d: ActivityDot }> = ({ d }) => {
  const title = `${d.lead_name} · ${d.kind}${d.outcome ? ` · ${d.outcome.replace(/_/g, ' ')}` : ''}`;
  if (d.kind === 'visit') return <span title={title} className="w-2.5 h-2.5 bg-gold rotate-45 inline-block" />;
  if (d.kind === 'call') {
    return d.outcome === 'connected'
      ? <span title={title} className="w-3 h-3 rounded-full bg-forest inline-block" />
      : <span title={title} className="w-3 h-3 rounded-full border-[1.5px] border-graphite inline-block" />;
  }
  if (d.kind === 'message') return <span title={title} className="w-3 h-3 rounded-[3px] bg-[#25A35A] inline-block" />;
  return <span title={title} className="w-3 h-3 rounded-sm bg-sky-wash inline-block" />;
};

const DashboardPage: React.FC = () => {
  const [followups, setFollowups] = useState<FollowupDot[] | null>(null);
  const [activity, setActivity] = useState<ActivityDot[] | null>(null);
  const [visitsAhead, setVisitsAhead] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fRange, setFRange] = useState<FRange>('4w');
  const [aRange, setARange] = useState<ARange>('7d');
  const [filter, setFilter] = useState('all');
  const [stage, setStage] = useState('all');
  const [dot, setDot] = useState<FollowupDot | null>(null);

  useEffect(() => {
    const now = new Date();
    const ago = (days: number) => new Date(now.getTime() - days * 86400000).toISOString();
    Promise.all([
      meApi.followups(ago(3650), new Date(now.getTime() + 92 * 86400000).toISOString()),
      meApi.activity(ago(28), new Date(now.getTime() + 5 * 60000).toISOString()),
      taskApi.list({ status: 'open', type: 'site_visit' }),
    ])
      .then(([f, a, v]) => { setFollowups(f.tasks); setActivity(a.activities); setVisitsAhead(v.tasks.length); })
      .catch((err) => setError(err?.response?.data?.detail || err.message));
  }, []);

  const inStage = useMemo(
    () => (followups || []).filter((d) => stage === 'all' || (d.stage || 'unknown') === stage), [followups, stage]);
  const match = FILTERS.find(([k]) => k === filter)![2];
  const buckets = useMemo(
    () => followupBuckets(inStage.filter(match), fRange), [inStage, match, fRange]);
  const visible = buckets.flatMap((b) => b.items);
  const aBuckets = useMemo(() => activityBuckets(activity || [], aRange), [activity, aRange]);

  if (error) return <div className="px-7 py-6"><Failed what="the dashboard" error={error} /></div>;
  if (!followups || !activity || visitsAhead === null) return <div className="px-7 py-6"><Loading what="the dashboard" /></div>;

  const visits28 = activity.filter((d) => d.kind === 'visit').length;
  const calls28 = activity.filter((d) => d.kind === 'call');
  const overdue = followups.filter((d) => new Date(d.due_at).getTime() < Date.now()).length;
  const stageCounts: Record<string, number> = {};
  followups.forEach((d) => { const s = d.stage || 'unknown'; stageCounts[s] = (stageCounts[s] || 0) + 1; });
  const shownActivity = aBuckets.flatMap((b) => b.items);

  const STAT = (v: React.ReactNode, k: string, head = false) => (
    <div className="px-3 first:pl-0 border-l-[1.5px] border-dotted border-sketch first:border-l-0">
      <div className={`font-display text-[24px] font-semibold num leading-tight ${head ? 'text-forest' : ''}`}>{v}</div>
      <div className="text-[11.5px] text-graphite mt-0.5">{k}</div>
    </div>
  );

  const stepDot = (delta: number) => {
    if (!dot) return;
    const i = visible.findIndex((d) => d.task_id === dot.task_id);
    setDot(visible[(i + delta + visible.length) % visible.length]);
  };

  return (
    <div className="w-full px-7 pt-6 pb-16">
      <div className="page-head">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">Site visits first, then the work that gets you there · times IST</p>
        </div>
      </div>

      <div className="card mb-3.5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-y-3">
          {STAT(visits28, 'Site visits · last 28 days', true)}
          {STAT(visitsAhead, 'Visits booked ahead')}
          {STAT(calls28.filter((d) => d.outcome === 'connected').length, 'Connected calls · 28 days')}
          {STAT(calls28.length, 'Dials · 28 days')}
          {STAT(overdue, 'Follow-ups overdue')}
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3.5">
        {[['all', followups.length] as const, ...Object.entries(stageCounts)].map(([k, n]) => (
          <button key={k} className="pill whitespace-nowrap" aria-pressed={stage === k} onClick={() => setStage(k)}>
            {k === 'all' ? 'All stages' : STAGE_LABEL[k] ?? label(k)} <span className="num opacity-70 ml-0.5">{n}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3.5 items-start mb-6">
        <div className="card">
          <h3 className="card-h">
            Follow-ups radar
            <span className="ml-auto flex gap-1">
              {(['7d', '4w', '3m'] as FRange[]).map((r) => (
                <button key={r} className="pill !text-[11.5px] !py-0.5 normal-case tracking-normal" aria-pressed={fRange === r} onClick={() => setFRange(r)}>
                  {{ '7d': '7 days', '4w': '4 weeks', '3m': '3 months' }[r]}
                </button>
              ))}
            </span>
          </h3>
          <div className="flex gap-1.5 flex-wrap mb-2">
            {FILTERS.map(([k, l, f]) => (
              <button key={k} className="pill" aria-pressed={filter === k} onClick={() => setFilter(k)}>
                {k === 'visit' && <span className="inline-block w-2 h-2 rounded-full ring-2 ring-gold mr-1.5 align-middle" />}
                {l} <span className="num opacity-70 ml-0.5">{inStage.filter(f).length}</span>
              </button>
            ))}
          </div>
          {buckets.map((b) => (
            <div key={b.key} className="grid grid-cols-[100px_minmax(0,1fr)_32px] gap-3 items-center py-1.5 border-b border-dotted border-sketch-soft last:border-b-0">
              <span>
                <span className={`block text-[12.5px] font-bold ${b.overdue && b.items.length ? 'text-brick' : ''}`}>{b.label}</span>
                {b.sub && <span className="block text-[11px] text-graphite">{b.sub}</span>}
              </span>
              <span className="flex flex-wrap gap-1.5">
                {b.items.map((d) => (
                  <button
                    key={d.task_id}
                    title={`${d.lead_name} · ${label(d.type)} · ${fmtDayTime(d.due_at)}`}
                    onClick={() => setDot(d)}
                    className={`w-4 h-4 rounded-full hover:scale-125 transition-transform ${DOT[stageChip(d.stage)]}
                      ${d.tags.some((t) => VISIT_INTENT.includes(t)) ? 'ring-2 ring-gold ring-offset-2 ring-offset-warm-white' : ''}`}
                  />
                ))}
              </span>
              <span className="num text-[12px] text-graphite text-right">{b.items.length}</span>
            </div>
          ))}
          <div className="flex gap-3.5 flex-wrap text-[11.5px] text-graphite mt-2.5">
            {(['chip-forest', 'chip-gold', 'chip-sky', 'chip-mute'] as const).map((c) => (
              <span key={c} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${DOT[c]}`} />
                {{ 'chip-forest': 'Visited & after', 'chip-gold': 'Visit wanted / fixed', 'chip-sky': 'In conversation', 'chip-mute': 'Other' }[c]}
              </span>
            ))}
            <span>Click a dot for the lead</span>
          </div>
        </div>

        <div className="card">
          <h3 className="card-h">
            Activity
            <span className="ml-auto flex gap-1">
              {(['7d', '4w'] as ARange[]).map((r) => (
                <button key={r} className="pill !text-[11.5px] !py-0.5 normal-case tracking-normal" aria-pressed={aRange === r} onClick={() => setARange(r)}>
                  {{ '7d': '7 days', '4w': '4 weeks' }[r]}
                </button>
              ))}
            </span>
          </h3>
          <div className="grid grid-cols-3 mb-3">
            {STAT(shownActivity.filter((d) => d.kind === 'visit').length, 'Site visits', true)}
            {STAT(shownActivity.filter((d) => d.kind === 'call' && d.outcome === 'connected').length, 'Connected')}
            {STAT(shownActivity.filter((d) => d.kind === 'call').length, 'Dials')}
          </div>
          <div className="flex gap-3.5 flex-wrap text-[11.5px] text-graphite mb-2">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-gold rotate-45 inline-block" />Site visit</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-forest inline-block" />Connected</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full border-[1.5px] border-graphite inline-block" />No answer</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-[3px] bg-[#25A35A] inline-block" />WhatsApp</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-sky-wash inline-block" />Note</span>
          </div>
          {aBuckets.map((b) => (
            <div key={b.key} className="grid grid-cols-[100px_minmax(0,1fr)] gap-3 items-center py-1.5 border-b border-dotted border-sketch-soft last:border-b-0">
              <span>
                <span className="block text-[12.5px] font-bold">{b.label}</span>
                {b.sub && <span className="block text-[11px] text-graphite">{b.sub}</span>}
              </span>
              <span className="flex flex-wrap gap-1 items-center">
                {b.items.length === 0 ? <span className="text-[11.5px] text-graphite">—</span> : b.items.map((d) => <Glyph key={d.activity_id} d={d} />)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <TagExplorer />

      {dot && (
        <Sheet
          title={dot.lead_name}
          sub={`${label(dot.type)} · due ${fmtDayTime(dot.due_at)}`}
          onClose={() => setDot(null)}
          footer={visible.length > 1 && (
            <>
              <span className="num text-[12px] text-graphite mr-auto self-center">
                {visible.findIndex((d) => d.task_id === dot.task_id) + 1} of {visible.length}
              </span>
              <button className="btn btn-sm" onClick={() => stepDot(-1)}>‹ Previous</button>
              <button className="btn btn-sm" onClick={() => stepDot(1)}>Next ›</button>
            </>
          )}
        >
          <LeadGlance key={dot.lead_id} leadId={dot.lead_id} />
        </Sheet>
      )}
    </div>
  );
};

export default DashboardPage;
